import { useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { NavLink } from '../components/nav';
import { PlaceMap } from '../features/map/PlaceMap';
import { PlaceCard } from '../features/places/PlaceCard';
import { PlaceList } from '../features/places/PlaceList';
import { Reader } from '../features/reader/Reader';
import { useAppData, useSortedPlaces } from '../state/AppData';
import { useSelection } from '../state/selection';

export function ReadView() {
  const { chapterId } = useParams();
  const [params] = useSearchParams();
  const { index, mentionsByPlace, city } = useAppData();
  const places = useSortedPlaces();
  const sel = useSelection();
  const [mobileTab, setMobileTab] = useState('texto');
  // En el móvil había dos barras de pestañas seguidas, con «Texto» repetido en las dos y
  // significados distintos. Ahora es una sola de cuatro, y la vista del lector viene de aquí.
  const [readerView, setReaderView] = useState<'text' | 'facsimile'>('text');
  const valid = index.chapters.some((c) => c.chapterId === chapterId);

  // al saltar a un pasaje, en móvil se vuelve a la pestaña de texto
  useEffect(() => { if (sel.jump) { setMobileTab('texto'); setReaderView('text'); } }, [sel.jump]);

  // en el móvil el mapa ocupa toda la pantalla: al elegir un lugar ahí, la ficha queda escondida
  // tras una pestaña y el recorrido se corta. Se pasa a ella, que es donde está el pasaje.
  useEffect(() => { if (sel.place && sel.origin === 'map') setMobileTab('lugar'); }, [sel.place, sel.origin]);

  if (!valid) return <Navigate to={`/leer/${index.chapters[0].chapterId}?${params}`} replace />;

  const chapterPlaces = new Set(
    [...mentionsByPlace.entries()].filter(([, ms]) => ms.some((m) => m.paragraphId.startsWith(chapterId!))).map(([id]) => id),
  );

  return (
    <div className="layout layout--read" data-mobile-tab={mobileTab}>
      <div className="mobile-tabs segmented" role="group" aria-label="Vista">
        {([
          ['texto', 'Texto', 'text'],
          ['original', 'Original', 'facsimile'],
          ['mapa', 'Mapa', null],
          ['lugar', 'Lugar', null],
        ] as const).map(([id, label, vista]) => {
          const activa = vista ? mobileTab === 'texto' && readerView === vista : mobileTab === id;
          return (
            <button key={id} type="button" aria-pressed={activa} onClick={() => {
              if (vista) { setMobileTab('texto'); setReaderView(vista); } else setMobileTab(id);
            }}>
              {label}{id === 'lugar' && sel.place ? ' •' : ''}
            </button>
          );
        })}
      </div>
      <nav className="pane pane--index" aria-label="Índice de capítulos">
        <h2>{city.section}</h2>
        <ol className="toc">
          {index.chapters.map((c) => (
            <li key={c.chapterId}>
              <NavLink to={`/leer/${c.chapterId}`} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>
                <span className="toc__num">{c.number}.</span> {c.title}
                <span className="toc__pages">pp. {c.printedPageStart}–{c.printedPageEnd}</span>
              </NavLink>
            </li>
          ))}
        </ol>
        <h2>Lugares en este capítulo</h2>
        <ul className="toc toc--places">
          {places.filter((p) => chapterPlaces.has(p.id)).map((p) => (
            <li key={p.id}>
              <button type="button" className="link-button" aria-pressed={sel.place === p.id} onClick={() => sel.selectPlace(p.id, 'list')}>
                {p.properties.canonicalName} <span className="count">{mentionsByPlace.get(p.id)?.filter((m) => m.paragraphId.startsWith(chapterId!)).length}</span>
              </button>
            </li>
          ))}
          {chapterPlaces.size === 0 && <li>Aún no hay lugares publicados en este capítulo.</li>}
        </ul>
      </nav>
      <div className="pane pane--reader">
        <Reader key={chapterId} chapterId={chapterId!} view={readerView} onView={setReaderView} />
      </div>
      <div className="pane pane--map">
        <PlaceMap places={places} />
        <details className="map-list">
          <summary>Lista de lugares (alternativa al mapa)</summary>
          <PlaceList places={places} />
        </details>
      </div>
      <div className="pane pane--card">
        <PlaceCard />
      </div>
    </div>
  );
}
